import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { entityAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';
import AdminLayout from '../../components/layouts/AdminLayout';

export default function EntityForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [loadingEntity, setLoadingEntity] = useState(isEdit);
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    pincode: ''
  });

  useEffect(() => {
    if (isEdit) {
      loadEntity();
    }
  }, [id]);

  const loadEntity = async () => {
    try {
      const response = await entityAPI.getAll();
      const entity = response.data.find(e => e.id === id);
      if (entity) {
        setFormData({
          name: entity.name,
          address: entity.address,
          city: entity.city,
          pincode: entity.pincode
        });
      } else {
        toast.error('Entity not found');
        navigate('/admin/entities');
      }
    } catch (error) {
      toast.error('Failed to load entity');
      navigate('/admin/entities');
    } finally {
      setLoadingEntity(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.address.trim() || !formData.city.trim() || !formData.pincode.trim()) {
      toast.error('All fields are required');
      return;
    }

    setLoading(true);
    try {
      if (isEdit) {
        await entityAPI.update(id, formData);
        toast.success('Community updated successfully');
      } else {
        await entityAPI.create(formData);
        toast.success('Community added successfully');
      }
      navigate('/admin/entities');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  if (loadingEntity) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button 
            onClick={() => navigate('/admin/entities')}
            className="p-2 hover:bg-muted rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-heading text-2xl font-bold">
            {isEdit ? 'Edit Community' : 'Add Community'}
          </h1>
        </div>

        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Community Name *</Label>
                <Input
                  id="name"
                  data-testid="entity-name"
                  placeholder="e.g., Prestige Lake Ridge"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address *</Label>
                <Input
                  id="address"
                  data-testid="entity-address"
                  placeholder="Full address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    data-testid="entity-city"
                    placeholder="e.g., Bangalore"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pincode">Pincode *</Label>
                  <Input
                    id="pincode"
                    data-testid="entity-pincode"
                    placeholder="e.g., 560100"
                    value={formData.pincode}
                    onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                  />
                </div>
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full rounded-full"
                disabled={loading}
                data-testid="save-entity-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  isEdit ? 'Update Community' : 'Add Community'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
